import { useCallback, useMemo, useRef, useState } from 'react';

import { IServerOrder } from '@rbi-ctg/menu';
import { CartPaymentCardType } from 'generated/graphql-gateway';
import { ICommitOrderInput } from 'hooks/commit-order/types';
import useEffectOnce from 'hooks/use-effect-once';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { IUseCartTip } from 'state/order/hooks/use-cart-tip';
import useRewards from 'state/rewards/use-rewards';
import { getConfigValue } from 'utils/environment';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';

import { useRequestApplePayPayment } from '../integrations/apple-pay';
import { getNonce } from '../integrations/first-data/client';
import { useFirstDataPrepaidReload } from '../integrations/first-data/use-first-data-prepaid-reload';
import { useRequestGooglePayPayment } from '../integrations/google-pay';
import { usePaymentContext } from '../state';
import { testCardPaymentState } from '../state';
import {
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  IPaymentMethod,
  IReloadPrepaidCard,
  PaymentRequestError,
  paymentErrorType,
} from '../types';
import { getFraudPreventionValues, isApplePay, isGooglePay, splitExpiry } from '../utils';

enum PaymentMode {
  ADDING_NEW_CARD,
  SELECTING_EXISTING_CARD,
}

interface IPrepaidMethodReload {
  commitOrder: (options: ICommitOrderInput) => void;
  handleCommitError: (error: any) => void;
  allPaymentMethods: IPaymentMethod[];
  user: UserDetails | null;
  serverOrder: IServerOrder | null;
  selectedPaymentMethod: IPaymentMethod | undefined;
  shouldCommitOrder: boolean;
  storeEmail: string;
  tipAmount: IUseCartTip['tipAmount'];
}

export const usePrepaidMethodReload = ({
  commitOrder,
  handleCommitError,
  allPaymentMethods,
  user,
  serverOrder,
  selectedPaymentMethod,
  shouldCommitOrder,
  storeEmail,
  tipAmount,
}: IPrepaidMethodReload) => {
  const {
    addPaymentMethod,
    checkoutPaymentMethodId,
    getBalanceFromPaymentMethods,
    getEncryptionDetails,
    getPaymentMethods,
    getPrepaidPaymentMethod,
    paymentMethods,
    prepaidReloadPaymentMethodId,
    reloadPrepaidCard,
    transformPaymentValues,
    reloadPaymentValues,
    setReloadPaymentValues,
  } = usePaymentContext();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const [needReloadAgain, setNeedReloadAgain] = useState(false);
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const fdUrl = getConfigValue({ key: 'firstData', defaultValue: {} })?.baseUrl;

  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const reloadCompletedRef = useRef(false);

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const accountIdentifier =
    prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '';
  const prepaidPaymentMethodSelected =
    !!accountIdentifier && checkoutPaymentMethodId === accountIdentifier;

  const { reloadAmount } = useRewards({
    prepaidCard: prepaidPaymentMethod,
    serverOrder,
    tipAmount,
    user,
  });

  // Autofill CC information
  useEffectOnce(() => {
    if (autoFill) {
      setReloadPaymentValues(testCardPaymentState({ paymentFieldVariations }));
    }
  });

  const showReloadPaymentMethods =
    enableGiftCard && prepaidPaymentMethodSelected && reloadAmount > 0;

  const allowedPaymentMethods = paymentMethods.filter(
    method => !method.prepaid && !method.cash && !method.paypal && !method.venmo
  );
  const mode: PaymentMode =
    allowedPaymentMethods.length > 0
      ? PaymentMode.SELECTING_EXISTING_CARD
      : PaymentMode.ADDING_NEW_CARD;

  const reloadWithApplePay = showReloadPaymentMethods && isApplePay(prepaidReloadPaymentMethodId);
  const reloadWithGooglePay = showReloadPaymentMethods && isGooglePay(prepaidReloadPaymentMethodId);

  const maybeAddNewCard = (): Promise<string | void> => {
    if (
      !reloadWithApplePay &&
      !reloadWithGooglePay &&
      reloadPaymentValues.saveCard &&
      mode === PaymentMode.ADDING_NEW_CARD
    ) {
      return addPaymentMethod(
        transformPaymentValues({
          paymentValues: reloadPaymentValues,
          paymentFieldVariations,
        })
      );
    }
    return Promise.resolve();
  };

  const getPrepaidBalance = (methods: IPaymentMethod[]) => {
    const prepaidAccount = methods.find((method: IPaymentMethod) => Boolean(method.prepaid));
    return getBalanceFromPaymentMethods(prepaidAccount!);
  };

  const maybeReloadGiftCard = async (
    input?: IApplePayDetails | IGooglePayDetails
  ): Promise<number> => {
    reloadCompletedRef.current = false;
    setNeedReloadAgain(false);

    const shouldReloadGiftCard = reloadAmount > 0 && prepaidPaymentMethodSelected;

    if (!shouldReloadGiftCard) {
      return Promise.resolve(getPrepaidBalance(allPaymentMethods));
    }

    // Add the new payment method if is necessary
    const paymentMethodId = (await maybeAddNewCard()) || prepaidReloadPaymentMethodId;

    const prepaidBalance = getPrepaidBalance(allPaymentMethods);
    const { countryCode } = getCountryAndCurrencyCodes(billingCountry);
    const prepaidFdAccountId =
      prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '';

    let args: IReloadPrepaidCard = {
      countryCode,
      fundValue: reloadAmount,
      prepaidBalance,
      prepaidFdAccountId,
      fdAccountId: paymentMethodId,
    };

    switch (paymentMethodId) {
      case CardTypes.APPLE_PAY:
        args = {
          ...args,
          applePayDetails: input as IApplePayDetails,
        };
        break;
      case CardTypes.GOOGLE_PAY:
        args = {
          ...args,
          googlePayDetails: input as IGooglePayDetails,
        };
        break;
    }

    // Return the original balance if reload failed, but only mark completion when the mutation
    // returned a balance. The caller uses this to recover from a later commit failure.
    const updatedBalance = await reloadPrepaidCard(args);
    if (updatedBalance !== undefined) {
      reloadCompletedRef.current = true;
    }
    return updatedBalance ?? prepaidBalance;
  };

  const buildCommitOrderPayload = async (prepaidBalance: number): Promise<ICommitOrderInput> => {
    if (!serverOrder) {
      return Promise.reject('Required parameters are not defined.');
    }

    if (prepaidBalance < serverOrder.cart.totalCents) {
      setNeedReloadAgain(true);
      return Promise.reject('Insufficient prepaid balance.');
    }

    if (prepaidPaymentMethodSelected) {
      const result: ICommitOrderInput = {
        fdAccountId:
          prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '',
      };
      return Promise.resolve(result);
    }

    if (reloadWithApplePay || reloadWithGooglePay) {
      const result: ICommitOrderInput = {};
      return Promise.resolve(result);
    }

    if (selectedPaymentMethod) {
      const result: ICommitOrderInput = {
        creditType: selectedPaymentMethod.credit
          ? ((selectedPaymentMethod.credit.cardType as unknown) as CartPaymentCardType)
          : null,
        fdAccountId:
          selectedPaymentMethod.accountIdentifier ?? selectedPaymentMethod.fdAccountId ?? '',
      };
      return Promise.resolve(result);
    }

    // Otherwise we have to jump through hoops to get more values from First Data
    const {
      fdAccessToken,
      fdApiKey,
      fdCustomerId,
      fdPublicKey,
      algorithm,
    } = await getEncryptionDetails();

    const reshapedPaymentValues = transformPaymentValues({
      paymentValues: reloadPaymentValues,
      paymentFieldVariations,
    });
    const nonceResponse = await getNonce(
      reshapedPaymentValues,
      fdPublicKey,
      fdApiKey,
      fdAccessToken,
      fdCustomerId ?? null,
      fdUrl,
      onlySendPostalCode,
      algorithm ?? undefined
    );
    const nonce = await nonceResponse.json();

    const fraudPreventionValues = getFraudPreventionValues(reloadPaymentValues);
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    const result: ICommitOrderInput = {
      creditType: reloadPaymentValues.cardType as CartPaymentCardType,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        firstDataInput: {
          accountIdentifier: reloadPaymentValues.saveCard ? accountIdentifier : undefined,
          fdAccessToken: reloadPaymentValues.saveCard ? undefined : fdAccessToken,
          fdNonce: reloadPaymentValues.saveCard ? undefined : nonce.token.tokenId,
        },
        fullName: fraudPreventionValues.fullName,
      },
    };
    return result;
  };

  const reloadGiftCardAndCheckout = async (data?: IApplePayDetails | IGooglePayDetails) => {
    // should never happen, since user cannot submit without
    // successfully pricing an order
    if (!serverOrder) {
      throw new Error('unreachable');
    }

    try {
      // reloads prepaid if necessary
      const currentPrepaidBalance = await maybeReloadGiftCard(data);

      if (shouldCommitOrder) {
        // commit order if prepaid balance is sufficient
        const commitOrderPayload = await buildCommitOrderPayload(currentPrepaidBalance);
        await commitOrder(commitOrderPayload);
      }
    } catch (e) {
      if (reloadCompletedRef.current) {
        // The reload mutation may have succeeded even when the subsequent order commit failed.
        // Refresh once so a retry uses the post-reload balance as its starting point.
        await getPaymentMethods({
          errorBehavior: 'log-only',
          source: 'prepaid-reload-commit-failed',
        });
      }
      logger.error(`Error reloading giftcard: ${e}`);
      handleCommitError(e);
    }
  };

  return {
    reloadAddPaymentMethod: mode === PaymentMode.ADDING_NEW_CARD,
    reloadAmount,
    reloadGiftCardAndCheckout,
    maybeReloadGiftCard,
    reloadPaymentValues,
    needReloadAgain,
    showReloadPaymentMethods,
    setReloadPaymentValues,
  };
};

export default usePrepaidMethodReload;

const getCheckoutPrepaidBalance = (prepaidPaymentMethod: IPaymentMethod | undefined) => {
  if (prepaidPaymentMethod?.prepaid?.feFormattedCurrentBalance) {
    return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
  }
  return prepaidPaymentMethod?.prepaid?.currentBalance ?? 0;
};

/** Provider-neutral prepaid reload orchestration used by permanent checkout. */
export const useCheckoutPrepaidReload = ({
  serverOrder,
  reloadAmount,
  billingCountry,
}: {
  serverOrder: IServerOrder | null;
  reloadAmount: number;
  billingCountry: ISOs;
}) => {
  const { checkoutPaymentMethod, prepaidReloadPaymentMethodId } = usePaymentContext();
  const { reloadWithFirstData } = useFirstDataPrepaidReload();
  const [isReloadLoading, setIsReloadLoading] = useState(false);

  const reloadWithApplePay = isApplePay(prepaidReloadPaymentMethodId);
  const reloadWithGooglePay = isGooglePay(prepaidReloadPaymentMethodId);
  const { countryCode } = getCountryAndCurrencyCodes(billingCountry);
  const prepaidFdAccountId =
    checkoutPaymentMethod?.accountIdentifier ?? checkoutPaymentMethod?.fdAccountId ?? '';

  const reloadInput: IReloadPrepaidCard = useMemo(
    () => ({
      countryCode,
      fdAccountId: prepaidReloadPaymentMethodId,
      fundValue: reloadAmount,
      prepaidBalance: getCheckoutPrepaidBalance(checkoutPaymentMethod),
      prepaidFdAccountId,
    }),
    [
      checkoutPaymentMethod,
      countryCode,
      prepaidFdAccountId,
      prepaidReloadPaymentMethodId,
      reloadAmount,
    ]
  );

  const { requestPayment: requestApplePayPayment } = useRequestApplePayPayment({
    applePay: {
      billingCountry,
      isForPrepaidReload: true,
      reloadAmount,
      serverOrder,
      tip: 0,
    },
  });
  const { requestPayment: requestGooglePayPayment } = useRequestGooglePayPayment({
    googlePay: {
      billingCountry,
      isForPrepaidReload: true,
      reloadAmount,
      total: 0,
    },
  });

  const reloadPrepaid = useCallback(async () => {
    if (reloadAmount === 0) {
      return;
    }

    setIsReloadLoading(true);
    try {
      if (reloadWithApplePay) {
        const paymentDetails = await requestApplePayPayment();
        if (paymentDetails?.applePayDetails) {
          await reloadWithFirstData(
            {
              ...reloadInput,
              applePayDetails: paymentDetails.applePayDetails,
            },
            'prepaid-reload-apple-pay'
          );
        }
        return;
      }

      if (reloadWithGooglePay) {
        const paymentDetails = await requestGooglePayPayment();
        if (!paymentDetails?.googlePayDetails) {
          throw new PaymentRequestError(paymentErrorType.googlePayCancelled);
        }

        await reloadWithFirstData(
          {
            ...reloadInput,
            googlePayDetails: paymentDetails.googlePayDetails,
          },
          'prepaid-reload-google-pay'
        );
        return;
      }

      await reloadWithFirstData(reloadInput, 'prepaid-reload-standard');
    } catch (error) {
      logger.error({ isPaymentLog: true, error, message: 'Error in reloadPrepaid' });
      throw error;
    } finally {
      setIsReloadLoading(false);
    }
  }, [
    reloadAmount,
    reloadInput,
    reloadWithApplePay,
    reloadWithFirstData,
    reloadWithGooglePay,
    requestApplePayPayment,
    requestGooglePayPayment,
  ]);

  return { isLoading: isReloadLoading, reloadPrepaid };
};
